import { Observable, Subscription } from 'rxjs';
import { AutocompletePage } from './autocomplete.request';

/** Users per page of the reviewer dropdown and the share sheet. */
export const USER_PAGE_SIZE = 20;

/**
 * Paged user suggestions for `app-autocomplete`: `search` loads the first
 * page of a text, `more` appends the next one while `hasMore` holds. The API
 * answers up to limit + 1 users; one more means another page. A new text
 * drops the pages and the requests of the old one; opening the list again
 * with the same text shows the loaded users without a request. Users that a
 * page repeats are skipped, and a page without new users ends the paging.
 */
export class UserPager<T extends { id: unknown }> {
  users: T[] = [];
  hasMore = false;
  private text = '';
  private offset = 0;
  private loaded = false;
  private loadingMore = false;
  private request?: Subscription;

  constructor(
    private fetch: (needle: string, page: AutocompletePage) => Observable<T[]>,
    private changed: () => void,
    private limit = USER_PAGE_SIZE
  ) {}

  search(query: string) {
    const text = query.trim();
    this.request?.unsubscribe();
    this.loadingMore = false;
    if (this.loaded && text === this.text) {
      // A new array, so the list opens again.
      this.users = [...this.users];
      this.changed();
      return;
    }
    this.text = text;
    this.loaded = false;
    this.offset = 0;
    this.request = this.fetch(text, { offset: 0, limit: this.limit }).subscribe(
      {
        next: (page) => this.apply(page, []),
        error: () => {
          this.users = [];
          this.hasMore = false;
          this.changed();
        },
      }
    );
  }

  more() {
    if (!this.loaded || !this.hasMore || this.loadingMore) {
      return;
    }
    this.loadingMore = true;
    const previous = this.users;
    this.request = this.fetch(this.text, {
      offset: this.offset,
      limit: this.limit,
    }).subscribe({
      next: (page) => this.apply(page, previous),
      error: () => {
        this.loadingMore = false;
        this.hasMore = false;
        this.changed();
      },
    });
  }

  /** Forgets the users and stops a running request. */
  reset() {
    this.request?.unsubscribe();
    this.users = [];
    this.hasMore = false;
    this.text = '';
    this.offset = 0;
    this.loaded = false;
    this.loadingMore = false;
  }

  destroy() {
    this.request?.unsubscribe();
  }

  private apply(page: T[], previous: T[]) {
    const shown = page.slice(0, this.limit);
    const known = new Set(previous.map((user) => user.id));
    const fresh = shown.filter((user) => !known.has(user.id));
    this.offset += shown.length;
    this.users = [...previous, ...fresh];
    this.hasMore = page.length > this.limit && fresh.length > 0;
    this.loaded = true;
    this.loadingMore = false;
    this.changed();
  }
}
